import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  listTransactions, createTransaction, updateTransaction, deleteTransaction,
  bulkDeleteTransactions,
} from '@/lib/api';
import {
  calculateCommission, formatTRY, formatDate, todayISO,
  SERVICE_LABELS, PAYMENT_LABELS,
} from '@/lib/commission';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { Pencil, Trash2, Plus, Info, Search, RefreshCw } from 'lucide-react';

const emptyForm = {
  transaction_date: todayISO(),
  customer_name: '',
  service_type: 'hand',
  amount: '',
  payment_method: 'cash',
};

function TransactionForm({ value, onChange, preview }) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-5">
      <div className="space-y-5 md:col-span-3">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="transaction_date" className="mb-1.5 block text-xs text-salon-muted">Tarih</Label>
            <Input
              id="transaction_date"
              type="date"
              data-testid="input-transaction-date"
              value={value.transaction_date}
              onChange={(e) => onChange({ ...value, transaction_date: e.target.value })}
              className="rounded-xl"
            />
          </div>
          <div>
            <Label htmlFor="customer_name" className="mb-1.5 block text-xs text-salon-muted">Müşteri Adı</Label>
            <Input
              id="customer_name"
              type="text"
              placeholder="Örn. Selin Serdar"
              data-testid="input-customer-name"
              value={value.customer_name}
              onChange={(e) => onChange({ ...value, customer_name: e.target.value })}
              className="rounded-xl"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label className="mb-1.5 block text-xs text-salon-muted">İşlem Türü</Label>
            <Select
              value={value.service_type}
              onValueChange={(v) => onChange({ ...value, service_type: v })}
            >
              <SelectTrigger data-testid="select-service-type" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hand" data-testid="option-service-hand">El (%70)</SelectItem>
                <SelectItem value="foot" data-testid="option-service-foot">Ayak (%30)</SelectItem>
                <SelectItem value="eyebrow" data-testid="option-service-eyebrow">Kaş (%50)</SelectItem>
                <SelectItem value="product" data-testid="option-service-product">Direk Satış/Ürün (%100)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="amount" className="mb-1.5 block text-xs text-salon-muted">Tutar (TL)</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="0,00"
              data-testid="input-amount"
              value={value.amount}
              onChange={(e) => onChange({ ...value, amount: e.target.value })}
              className="rounded-xl"
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs text-salon-muted">Ödeme Şekli</Label>
            <Select
              value={value.payment_method}
              onValueChange={(v) => onChange({ ...value, payment_method: v })}
            >
              <SelectTrigger data-testid="select-payment-method" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash" data-testid="option-payment-cash">Nakit</SelectItem>
                <SelectItem value="credit_card" data-testid="option-payment-card">Kredi Kartı</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Live preview */}
      <Card className="rounded-2xl border-salon-secondary bg-salon-secondary/25 md:col-span-2" data-testid="preview-card">
        <CardContent className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-salon-primary-hover">Anlık Hesaplama</span>
            <Badge variant="outline" className="rounded-full border-salon-primary/40 text-salon-primary-hover">
              {PAYMENT_LABELS[value.payment_method]}
            </Badge>
          </div>
          <div className="space-y-2 text-sm">
            <Row label="Brüt Tutar (KDV Dahil)" value={formatTRY(preview.gross)} />
            {value.payment_method === 'credit_card' && (
              <>
                <Row label="KDV (%20)" value={formatTRY(preview.vat)} muted />
                <Row label="KDV Hariç Tutar" value={formatTRY(preview.vat_excluded)} />
                <Row label="Banka Komisyonu (%3)" value={`- ${formatTRY(preview.bank_commission)}`} muted />
                <Row label="Net Tutar" value={formatTRY(preview.net)} />
              </>
            )}
            <div className="my-2 h-px bg-salon-primary/15" />
            <Row
              label={`Personel Payı (%${Math.round(preview.staff_rate * 100)})`}
              value={formatTRY(preview.staff_share)}
              strong
              testId="preview-staff-share"
            />
            <Row
              label={`Merkez Payı (%${Math.round(preview.center_rate * 100)})`}
              value={formatTRY(preview.center_share)}
              strong
              testId="preview-center-share"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const Row = ({ label, value, muted, strong, testId }) => (
  <div className="flex items-center justify-between">
    <span className={`${muted ? 'text-salon-muted' : 'text-salon-ink/80'} text-sm`}>{label}</span>
    <span
      data-testid={testId}
      className={`${strong ? 'number-display text-base text-salon-ink' : 'text-sm text-salon-ink'}`}
    >
      {value}
    </span>
  </div>
);

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [filters, setFilters] = useState({ start_date: '', end_date: '', customer_name: '' });

  const preview = useMemo(
    () => calculateCommission(Number(form.amount) || 0, form.service_type, form.payment_method),
    [form]
  );
  const editPreview = useMemo(
    () => editing ? calculateCommission(Number(editing.amount) || 0, editing.service_type, editing.payment_method) : null,
    [editing]
  );

  const load = () => {
    setLoading(true);
    const params = {};
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.customer_name) params.customer_name = filters.customer_name;
    listTransactions(params)
      .then((data) => { setItems(data); setSelectedIds(new Set()); })
      .catch(() => toast.error('İşlemler yüklenemedi'))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) { toast.error('Müşteri adı gerekli'); return; }
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error('Geçerli bir tutar girin'); return; }
    try {
      await createTransaction({
        transaction_date: form.transaction_date,
        customer_name: form.customer_name.trim(),
        service_type: form.service_type,
        amount: amt,
        payment_method: form.payment_method,
      });
      toast.success('İşlem eklendi');
      setForm({ ...emptyForm, transaction_date: form.transaction_date });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kaydedilemedi');
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await updateTransaction(editing.id, {
        transaction_date: editing.transaction_date,
        customer_name: editing.customer_name,
        service_type: editing.service_type,
        amount: Number(editing.amount),
        payment_method: editing.payment_method,
      });
      toast.success('İşlem güncellendi');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Güncellenemedi');
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteTransaction(confirmDelete.id);
      toast.success('İşlem silindi');
      setConfirmDelete(null);
      load();
    } catch {
      toast.error('Silinemedi');
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(items.map((t) => t.id)));
    else setSelectedIds(new Set());
  };

  const doBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const res = await bulkDeleteTransactions(ids);
      toast.success(`${res.deleted_count} işlem silindi`);
      setConfirmBulkDelete(false);
      setSelectedIds(new Set());
      load();
    } catch {
      toast.error('Toplu silme başarısız');
    }
  };

  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <div className="space-y-8" data-testid="transactions-page">
      <header className="flex flex-col gap-2">
        <div className="text-xs uppercase tracking-widest text-salon-primary">Kayıt</div>
        <h1 className="font-display text-4xl text-salon-ink md:text-5xl">İşlemler</h1>
        <p className="text-sm text-salon-muted">Yeni işlem ekleyin, geçmişi düzenleyin ve filtreleyin.</p>
      </header>

      <Card className="rounded-2xl border-border shadow-soft">
        <CardContent className="p-6 md:p-8">
          <form onSubmit={submit} className="space-y-6">
            <TransactionForm value={form} onChange={setForm} preview={preview} />
            <div className="flex justify-end">
              <Button
                type="submit"
                data-testid="submit-transaction-btn"
                className="rounded-full bg-salon-primary px-6 hover:bg-salon-primary-hover"
              >
                <Plus size={16} className="mr-1.5" />
                İşlemi Kaydet
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="rounded-2xl border-border shadow-soft">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-5">
            <div>
              <Label className="mb-1.5 block text-xs text-salon-muted">Başlangıç Tarihi</Label>
              <Input
                type="date"
                data-testid="filter-start-date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs text-salon-muted">Bitiş Tarihi</Label>
              <Input
                type="date"
                data-testid="filter-end-date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="mb-1.5 block text-xs text-salon-muted">Müşteri Adı</Label>
              <Input
                type="text"
                placeholder="Ara..."
                data-testid="filter-customer-name"
                value={filters.customer_name}
                onChange={(e) => setFilters({ ...filters, customer_name: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={load}
                data-testid="apply-filter-btn"
                className="flex-1 rounded-full bg-salon-ink text-white hover:bg-salon-ink/90"
              >
                <Search size={15} className="mr-1.5" /> Ara
              </Button>
              <Button
                variant="outline"
                data-testid="reset-filter-btn"
                onClick={() => { setFilters({ start_date: '', end_date: '', customer_name: '' }); setTimeout(load, 0); }}
                className="rounded-full"
              >
                <RefreshCw size={15} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-2xl border-border shadow-soft" data-testid="transactions-table-card">
        <CardContent className="p-0">
          {selectedIds.size > 0 && (
            <div
              data-testid="bulk-action-bar"
              className="flex items-center justify-between border-b border-border bg-salon-secondary/50 px-6 py-3"
            >
              <span className="text-sm text-salon-ink" data-testid="bulk-selection-count">
                {selectedIds.size} işlem seçildi
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedIds(new Set())}
                  className="rounded-full text-salon-ink"
                  data-testid="bulk-clear-btn"
                >
                  Seçimi Temizle
                </Button>
                <Button
                  onClick={() => setConfirmBulkDelete(true)}
                  className="rounded-full bg-red-600 text-white hover:bg-red-700"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 size={15} className="mr-1.5" />
                  Seçilenleri Sil
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="w-12 pl-6">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                      onCheckedChange={(v) => toggleAll(!!v)}
                      data-testid="select-all-checkbox"
                      aria-label="Tümünü seç"
                    />
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-salon-muted">Tarih</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-salon-muted">Müşteri</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-salon-muted">İşlem</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-salon-muted">Ödeme</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-widest text-salon-muted">Tutar</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-widest text-salon-muted">Personel</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-widest text-salon-muted">Merkez</TableHead>
                  <TableHead className="pr-6 text-right text-xs uppercase tracking-widest text-salon-muted">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-salon-muted">Yükleniyor...</TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-salon-muted" data-testid="empty-state">Henüz işlem eklenmedi.</TableCell></TableRow>
                ) : items.map((t) => (
                  <TableRow key={t.id} data-testid={`row-${t.id}`} className="border-border">
                    <TableCell className="pl-6 py-4">{formatDate(t.transaction_date)}</TableCell>
                    <TableCell className="font-medium">{t.customer_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-full border-salon-primary/30 bg-salon-secondary/40 text-salon-primary-hover">
                        {SERVICE_LABELS[t.service_type]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm">{PAYMENT_LABELS[t.payment_method]}</span>
                        {t.payment_method === 'credit_card' && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button type="button" className="text-salon-muted hover:text-salon-ink" data-testid={`tooltip-${t.id}`}>
                                  <Info size={14} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="rounded-xl">
                                <div className="text-xs">
                                  <div>KDV Hariç: {formatTRY(t.vat_excluded ?? (t.amount / 1.2))}</div>
                                  <div>KDV: {formatTRY(t.vat)}</div>
                                  <div>Banka Kom.: {formatTRY(t.bank_commission)}</div>
                                  <div>Net: {formatTRY(t.net_amount)}</div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right number-display">{formatTRY(t.amount)}</TableCell>
                    <TableCell className="text-right number-display text-salon-primary-hover">{formatTRY(t.staff_share)}</TableCell>
                    <TableCell className="text-right number-display">{formatTRY(t.center_share)}</TableCell>
                    <TableCell className="pr-6 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => setEditing({ ...t })}
                          data-testid={`edit-btn-${t.id}`}
                          className="h-9 w-9 rounded-full text-salon-muted hover:bg-salon-secondary/40 hover:text-salon-primary-hover"
                          aria-label="Düzenle"
                        ><Pencil size={15} /></Button>
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => setConfirmDelete(t)}
                          data-testid={`delete-btn-${t.id}`}
                          className="h-9 w-9 rounded-full text-salon-muted hover:bg-red-50 hover:text-red-600"
                          aria-label="Sil"
                        ><Trash2 size={15} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-3xl rounded-2xl" data-testid="edit-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">İşlemi Düzenle</DialogTitle>
          </DialogHeader>
          {editing && (
            <TransactionForm
              value={editing}
              onChange={setEditing}
              preview={editPreview}
            />
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full" data-testid="cancel-edit-btn">İptal</Button>
            <Button onClick={saveEdit} className="rounded-full bg-salon-primary hover:bg-salon-primary-hover" data-testid="save-edit-btn">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Bu işlem silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  <strong>{confirmDelete.customer_name}</strong> — {SERVICE_LABELS[confirmDelete.service_type]} — {formatTRY(confirmDelete.amount)}
                  <br />
                  Bu işlem geri alınamaz.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn" className="rounded-full">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="confirm-delete-btn"
              className="rounded-full bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={confirmBulkDelete} onOpenChange={setConfirmBulkDelete}>
        <AlertDialogContent className="rounded-2xl" data-testid="bulk-delete-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Seçilen işlemler silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{selectedIds.size}</strong> işlem kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-bulk-delete-btn" className="rounded-full">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={doBulkDelete}
              data-testid="confirm-bulk-delete-btn"
              className="rounded-full bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
